import { createSlice } from '@reduxjs/toolkit';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';

/**
 * Tracks exchanges with a changelog sync currently in progress, driven by the
 * assistant WS channel's `sync-started`/`sync-finished` events (see the
 * dashboard's ws-assistant-effects) plus an optimistic `syncStarted` dispatch
 * from the triggering client. Shared store state so every client shows an
 * accurate spinner — including for syncs triggered elsewhere (scheduler,
 * other admins).
 */
export interface ExchangeSyncState {
  syncingIds: Record<number, true>;
}

export const initialState: ExchangeSyncState = {
  syncingIds: {},
};

const exchangeSyncSlice = createSlice({
  name: 'exchangeSync',
  initialState,
  reducers: {
    syncStarted(state, action: PayloadAction<number>) {
      state.syncingIds[action.payload] = true;
    },
    syncFinished(state, action: PayloadAction<number>) {
      delete state.syncingIds[action.payload];
    },
  },
});

export const exchangeSyncActions = exchangeSyncSlice.actions;

/** Any action created by {@link exchangeSyncActions}. */
export type ExchangeSyncAction = ReturnType<
  (typeof exchangeSyncActions)[keyof typeof exchangeSyncActions]
>;

/**
 * A dispatch narrowed to this slice's actions, for consumers that only write
 * this slice (`useDispatch<ExchangeSyncDispatch>()`). The app's root dispatch
 * satisfies it (checked in `store/package-store-contracts.test.ts`).
 */
export type ExchangeSyncDispatch = Dispatch<ExchangeSyncAction>;

export default exchangeSyncSlice.reducer;
